import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";

const territoryData = [
  { territory: "NCR", target: 5800000, actual: 5450000, achievement: 93.9 },
  { territory: "Luzon N.", target: 3200000, actual: 3650000, achievement: 114.1 },
  { territory: "Luzon S.", target: 2800000, actual: 2410000, achievement: 86.1 },
  { territory: "Visayas", target: 2600000, actual: 2480000, achievement: 95.4 },
  { territory: "Mindanao", target: 2200000, actual: 2080000, achievement: 94.5 },
];

const formatPHP = (v) => `₱${(v / 1000000)?.toFixed(1)}M`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = territoryData?.find((d) => d?.territory === label);
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg min-w-[180px]">
      <p className="font-caption text-xs font-semibold mb-2" style={{ color: '#0F172A' }}>{label}</p>
      {payload?.map((p) => (
        <div key={p?.dataKey} className="flex items-center justify-between gap-4 mb-1">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p?.fill }} />
            <span className="font-caption text-xs" style={{ color: '#475569' }}>{p?.name}</span>
          </div>
          <span className="font-data text-xs font-medium" style={{ color: '#1E293B' }}>{formatPHP(p?.value)}</span>
        </div>
      ))}
      {row && (
        <div className="mt-2 pt-2 border-t border-border">
          <span className={`font-caption text-xs font-semibold ${row?.achievement >= 100 ? "text-emerald-600" : "text-amber-600"}`}>
            {row?.achievement}% of target
          </span>
        </div>
      )}
    </div>
  );
};

export default function TerritoryBarChart() {
  return (
    <div className="card p-4 md:p-6 chart-panel-depth">
      <div className="mb-4">
        <h3 className="font-heading text-lg md:text-xl font-semibold" style={{ color: '#0F172A', letterSpacing: '0.01em' }}>Territory Performance</h3>
        <p className="font-caption text-sm mt-0.5" style={{ color: '#475569' }}>Target vs Actual — March 2026</p>
      </div>
      <div className="w-full chart-height-standard" aria-label="Territory Performance Bar Chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={territoryData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.6} vertical={false} />
            <XAxis
              dataKey="territory"
              tick={{ fontSize: 11, fontFamily: "var(--font-caption)", fill: "#374151", fontWeight: 600 }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={formatPHP}
              tick={{ fontSize: 11, fontFamily: "var(--font-data)", fill: "#374151", fontWeight: 600 }}
              width={52}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontFamily: "var(--font-caption)", fontSize: "13px", paddingTop: "8px", color: '#1A1A2E', fontWeight: 600 }}
            />
            <Bar dataKey="target" name="Target" fill="#CBD5E1" radius={[4, 4, 0, 0]} maxBarSize={32}
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.10))' }}
            />
            <Bar dataKey="actual" name="Actual" radius={[4, 4, 0, 0]} maxBarSize={32}
              style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.15))' }}
            >
              {territoryData?.map((entry) => (
                <Cell
                  key={entry?.territory}
                  fill={entry?.achievement >= 100 ? "#059669" : entry?.achievement >= 90 ? "#DC2626" : "#D4AF37"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}